import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UnitCategoryCode, FlamingoFund, FundManagerPhotoData } from 'funds-model';
import FundDetailsDialogAccordion from './FundDetailsDialogAccordion';
import { useDispatch } from 'react-redux';
import { Title } from '../Title/Title';
import { Typography } from '../Typography/Typography';
import { useIsMobile } from '../utils/useIsMobile';
import { Label } from '../Label/Label';

import useStyles from './FundDetailsDialog.style';

export interface FundDetailsDialogInformationProps {
  fund: FlamingoFund;
  unitCategoryCode: UnitCategoryCode;
  getFundManagerPhotoPromise: (peopleIds: string) => Promise<FundManagerPhotoData[]>;
}

const FundDetailsDialogInformation: React.FC<FundDetailsDialogInformationProps> = ({ fund, unitCategoryCode, getFundManagerPhotoPromise }) => {
  const c = useStyles({});
  const [t] = useTranslation('components');
  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  const attributesData = fund.attributes ? fund.attributes.map(attribute => {
    return {
      key: attribute.key,
      value: attribute.values.find(obj => obj.unitCategoryCode === unitCategoryCode)?.value,
      seq: attribute.seq
    }
  }).sort((attr1, attr2) => attr1.seq - attr2.seq) : [];
  const columnIndex = Math.ceil(attributesData.length / 2);
  const firstColumnData = attributesData.slice(0, columnIndex);
  const secondColumnData = attributesData.slice(columnIndex);

  const [fundManagersData, setFundManagersData] = useState<FundManagerPhotoData[] | undefined>();
  useEffect(() => {
    if ((!fund.fundManagersPhoto || fund.fundManagersPhoto.length === 0) && (fund.fundManagers && fund.fundManagers.length > 0)) {
      const peopleIdsString = fund.fundManagers.map(manager => manager.peopleId).join(', ');
      getFundManagerPhotoPromise(peopleIdsString).then(res => {
        setFundManagersData(res);
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fund.fundManagers.length, fund.id, dispatch]);

  const fundManagersDisplayData = [...((fund.fundManagersPhoto && fund.fundManagersPhoto.length > 0) ? fund.fundManagersPhoto : (fundManagersData || []))]
      .sort((a, b) => {
        const seqA = fund.fundManagers.find(fundManager => fundManager.peopleId === a.id)?.seq || 0;
        const seqB = fund.fundManagers.find(fundManager => fundManager.peopleId === b.id)?.seq || 0;
        return seqA - seqB;
      });

  return (
    <FundDetailsDialogAccordion title={t('fund-details-dialog.information.title')}>
      <div>
        {!isMobile && <Title titleComponent="h3" titleFontWeight="bold" titleVariant='h3' title={t('fund-details-dialog.information.title')} />}
        <div className={c.infoGrid}>
          <div>
            {firstColumnData.map(data => (
              <div key={data.key}>
                <Label className={c.infoLabel} color="graphite" size="s">{data.key}</Label>
                <span className={c.infoValue}>{data.value}</span>
              </div>
            ))}
          </div>
          <div>
            {secondColumnData.map(data => (
              <div key={data.key}>
                <Label className={c.infoLabel} color="graphite" size="s">{data.key}</Label>
                <span className={c.infoValue}>{data.value}</span>
              </div>
            ))}
          </div>
        </div>
        {!!fundManagersDisplayData && fundManagersDisplayData.length > 0 && <div>
          <Typography
            component="h5"
            variant="body1"
            fontWeight="bold"
            className={c.fundManagerTitle}
          >
            {t('fund-details-dialog.managers')}
          </Typography>
          <div className={c.fundManagerGrid}>
            {fundManagersDisplayData?.map(managerData => (
              <div className={c.fundManagerBox} key={`${managerData.nameAndSurname}-${managerData.position}`}>
                <div className={c.fundManagerImageBox}>
                  {managerData?.photoBase64 && <img src={`data:${managerData.photoContentType};base64, ${managerData?.photoBase64}`} alt={managerData.photoAlt} />}
                </div>
                <div className={c.fundManagerNameBox}>
                  <Typography component="span" variant="body1" fontWeight="bold">{managerData?.nameAndSurname}</Typography>
                  <Label size="s" color="graphite">{managerData?.position}</Label>
                </div>
              </div>
            ))}
          </div>
        </div>}
      </div>
    </FundDetailsDialogAccordion>
  );
};

export default FundDetailsDialogInformation;
